import React, { useState, useEffect } from 'react';
import './App.css';
import UniversalUpload from './components/UniversalUpload';
import ResultsDisplay from './components/ResultsDisplay';
import ExcelValidation from './components/ExcelValidation';

function App() {
  const [processingResults, setProcessingResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState('upload'); // 'upload', 'validation', 'results'

  useEffect(() => {
    // Fetch webhook status on component mount
    fetch('/api/webhook/status')
      .then(response => response.json())
      .then(data => setWebhookStatus(data))
      .catch(err => console.error('Failed to fetch webhook status:', err));
  }, []);

  const handleFileUpload = async (files) => {
    setIsProcessing(true);
    setError(null);
    setProcessingResults(null);

    try {
      const formData = new FormData();
      const fileArray = Array.isArray(files) ? files : [files];
      
      if (fileArray.length === 1) {
        // Single file processing
        formData.append('invoice', fileArray[0]);

        const response = await fetch('/api/process-invoice', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to process invoice');
        }

        const result = await response.json();
        setProcessingResults(result);
      } else {
        // Multiple files processing
        fileArray.forEach(file => {
          formData.append('invoices', file);
        });

        const response = await fetch('/api/process-multiple-invoices', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to process invoices');
        }

        const result = await response.json();
        setProcessingResults(result);
      }
      
      setCurrentStep('validation'); // Move to validation step
    } catch (err) {
      setError(err.message);
      console.error('Error processing invoice:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleValidationConfirm = async (validatedInvoiceData) => {
    setIsProcessing(true);

    try {
      // Determine the type based on the data structure and results
      let requestType = 'single-invoice';
      
      if (Array.isArray(validatedInvoiceData)) {
        if (validatedInvoiceData.length > 1) {
          // Check if we have session ID to determine if it's multi-file or multi-invoice
          if (validatedInvoiceData[0]?._sessionId) {
            requestType = 'multi-file';
          } else {
            requestType = 'multi-invoice';
          }
        } else if (validatedInvoiceData.length === 1) {
          // Single item in array - check for session ID
          if (validatedInvoiceData[0]?._sessionId) {
            requestType = 'multi-file';
          } else {
            requestType = 'single-invoice';
          }
        }
      }

      // Send validated data to generate Excel/ZIP with proper structure
      const response = await fetch('/api/generate-final', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: requestType,
          data: validatedInvoiceData
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate final files');
      }

      const result = await response.json();
      
      // Update results with session ID for download, preserving the original data structure
      setProcessingResults(prev => ({
        ...prev,
        sessionId: result.sessionId,
        data: prev.type === 'multi-file' ? prev.data : prev.data, // Keep original data structure
        validated: true
      }));
      
      setCurrentStep('results');
    } catch (err) {
      setError(err.message);
      console.error('Error generating final files:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleValidationCancel = () => {
    setCurrentStep('upload');
    setProcessingResults(null);
  };

  const handleBackToValidation = () => {
    setCurrentStep('validation');
  };

  const handleReset = () => {
    setProcessingResults(null);
    setCurrentStep('upload');
    setError(null);
  };

  const renderCurrentStep = () => {
    if (isProcessing) {
      return (
        <div className="processing-container">
          <div className="loading-spinner"></div>
          <h3>
            {currentStep === 'upload' ? 
              'Обработване на фактури...' : 
              'Генериране на файлове...'
            }
          </h3>
          <p>
            {currentStep === 'upload' 
              ? 'Processing invoices with Azure AI...' 
              : 'Generating Excel and ZIP files...'}
          </p>
          <div className="processing-steps">
            {currentStep === 'upload' ? (
              <>
                <div className="step">1. Извличане на текст с OCR / Extracting text with OCR</div>
                <div className="step">2. Анализ с Azure AI / Analyzing with Azure AI</div>
                <div className="step">3. Структуриране на данните / Structuring data</div>
              </>
            ) : (
              <>
                <div className="step">1. Валидация на данните / Validating data</div>
                <div className="step">2. Генериране на Excel / Generating Excel</div>
                <div className="step">3. Създаване на ZIP архив / Creating ZIP archive</div>
              </>
            )}
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case 'upload':
        return (
          <UniversalUpload 
            onFileUpload={handleFileUpload} 
            isProcessing={isProcessing} 
          />
        );
      
      case 'validation':
        // Always use Excel validation for unified interface
        return (
          <ExcelValidation
            results={processingResults}
            onConfirm={handleValidationConfirm}
            onCancel={handleValidationCancel}
          />
        );
      
      case 'results':
        return (
          <ResultsDisplay 
            results={processingResults} 
            onReset={handleReset}
            onBackToValidation={handleBackToValidation}
          />
        );
      
      default:
        return (
          <UniversalUpload 
            onFileUpload={handleFileUpload} 
            isProcessing={isProcessing} 
          />
        );
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>🧾 Invoice Processor</h1>
        <p>Система за обработка на фактури / Bulgarian Invoice Processing System</p>
        
        {webhookStatus && (
          <div className="webhook-status">
            <div className={`status-indicator ${webhookStatus.enabled ? 'enabled' : 'disabled'}`}>
              {webhookStatus.enabled ? '🟢' : '🔴'} 
              Webhook: {webhookStatus.enabled ? 'Активен / Active' : 'Неактивен / Inactive'}
            </div>
            {webhookStatus.enabled && webhookStatus.url && (
              <div 
                className="webhook-url" 
                title="Click to copy webhook URL"
                onClick={() => navigator.clipboard?.writeText(webhookStatus.url)}
              >
                {webhookStatus.url}
              </div>
            )}
          </div>
        )}

        {/* Step Indicator */}
        <div className="step-indicator">
          <div className={`step-item ${currentStep === 'upload' ? 'active' : currentStep === 'validation' || currentStep === 'results' ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">
              <div>Качване</div>
              <div className="step-sublabel">Upload</div>
            </div>
          </div>
          <div className="step-arrow">→</div>
          <div className={`step-item ${currentStep === 'validation' ? 'active' : currentStep === 'results' ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">
              <div>Валидация</div>
              <div className="step-sublabel">Validation</div>
            </div>
          </div>
          <div className="step-arrow">→</div>
          <div className={`step-item ${currentStep === 'results' ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">
              <div>Резултати</div>
              <div className="step-sublabel">Results</div>
            </div>
          </div>
        </div>
      </header>

      <main className="App-main">
        {error && (
          <div className="error-container">
            <div className="error-message">
              <h3>⚠️ Възникна грешка / Error Occurred</h3>
              <p>{error}</p>
              <button onClick={() => setError(null)} className="dismiss-error">
                Затвори / Dismiss
              </button>
            </div>
          </div>
        )}

        {renderCurrentStep()}
      </main>

      <footer className="App-footer">
        <p>
          &copy; 2024 Invoice Processor. 
          Powered by Azure AI and OpenAI • 
          <a href="https://github.com" target="_blank" rel="noopener noreferrer">
            GitHub
          </a>
        </p>
      </footer>
    </div>
  );
}

export default App;
